import React, { useEffect, memo, useState, useCallback } from 'react';
import * as Styled from './styled';
import {
  ReportField,
  ReportSnapshot,
  SavedReportDetail,
} from '../../../models';
import { useRuleBuilderReducer } from '../../create-report/reducers';
import RuleBuilder from '../../create-report/rule-builder/RuleBuilder';
import { useAuth } from '../../../context';
import { StyledText } from '@leasequery/lq.react-component-library';
import { createRuleBuilderDefaultFields } from '../../helpers';
import { fetchReportDefaultFieldsWithCache } from '../../../api/report/fetchReportDefaultFields';
import { noop } from '../../../util';
import RuleBuilderDragDropContext, {
  RULEBUILDER_DROPPABLE_ID,
} from '../../create-report/rule-builder/rule-builder-dragdrop-context/RuleBuilderDragDropContext';
import { DropResult } from 'react-beautiful-dnd';
import { LoadingSkeleton, Icon } from '../../../components';
import { areFieldsEqual } from '../helpers';

interface StandardReportIncludedFieldsProps {
  reportSnapshot?: ReportSnapshot;
  standardReportId?: number;
  savedReportDetail?: SavedReportDetail;
  sharedReportDetail?: SavedReportDetail;
  onChange: (fields: ReportField[]) => void;
  onAddFieldsButtonCLick?: () => void;
  reportHasUnsavedChanges: (val: boolean) => void;
}

function StandardReportIncludedFields({
  reportSnapshot,
  standardReportId,
  savedReportDetail,
  sharedReportDetail,
  onChange,
  onAddFieldsButtonCLick,
  reportHasUnsavedChanges,
}: StandardReportIncludedFieldsProps): JSX.Element {
  const { client } = useAuth();
  const clientId = client.clientId;

  const [isLoading, setIsLoading] = useState(true);
  const [hasDefaultFields, setHasDefaultFields] = useState(true);
  const [initialFieldsToCompareTo, setInitialFieldsToCompareTo] =
    useState(null);

  const [
    { fields },
    {
      updateRuleBuilderFields,
      removeRuleBuilderField,
      addRuleToRuleBuilderField,
      cancelFieldEditing,
      saveEditingFields,
      removeRule,
      updateRule,
      turnOnRuleEditingMode,
    },
  ] = useRuleBuilderReducer();

  useEffect(() => {
    const abortController = new AbortController();

    const fetchData = async (): Promise<void> => {
      setHasDefaultFields(true);
      setIsLoading(true);

      try {
        const reportDefaultFields = await fetchReportDefaultFieldsWithCache(
          standardReportId,
          clientId,
        );

        // Check if this request was aborted (component unmounted or deps changed)
        if (abortController.signal.aborted) {
          return;
        }

        const defaultFields = createRuleBuilderDefaultFields(
          reportDefaultFields,
          reportSnapshot,
          savedReportDetail,
          sharedReportDetail,
        );

        updateRuleBuilderFields(defaultFields);
        setInitialFieldsToCompareTo(defaultFields);
        setIsLoading(false);
        setHasDefaultFields(
          defaultFields.filter((f) => !f.isAllocation).length > 0,
        );
      } catch (error) {
        // Only handle errors if request wasn't aborted
        if (!abortController.signal.aborted && error) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    // Cleanup function to abort the request when dependencies change
    return () => {
      abortController.abort();
    };
  }, [
    clientId,
    reportSnapshot,
    savedReportDetail,
    sharedReportDetail,
    standardReportId,
    updateRuleBuilderFields,
  ]);

  useEffect(() => {
    onChange(fields);

    // compare fields to initialFieldsToCompareTo
    if (initialFieldsToCompareTo && !isLoading) {
      reportHasUnsavedChanges(
        !areFieldsEqual(fields, initialFieldsToCompareTo),
      );
    }
  }, [
    fields,
    onChange,
    initialFieldsToCompareTo,
    reportHasUnsavedChanges,
    isLoading,
  ]);

  const dragEndHandler = useCallback(
    (result: DropResult) => {
      if (result?.destination?.droppableId !== RULEBUILDER_DROPPABLE_ID) {
        return;
      }
      const nextFields = [...fields];
      const items = nextFields.splice(result.source.index, 1);
      nextFields.splice(result.destination.index, 0, ...items);
      updateRuleBuilderFields(nextFields);
    },
    [fields, updateRuleBuilderFields],
  );

  return (
    hasDefaultFields && (
      <RuleBuilderDragDropContext onDragEnd={dragEndHandler}>
        <Styled.StandardReportIncludedFieldsTitleBar>
          <div>
            <Icon name="report" />
          </div>
          <StyledText fontWeight="medium">Included Fields</StyledText>
        </Styled.StandardReportIncludedFieldsTitleBar>
        {isLoading ? (
          <LoadingSkeleton name="table" />
        ) : (
          <>
            <RuleBuilder
              fields={fields}
              onRemoveField={removeRuleBuilderField}
              onAddRule={addRuleToRuleBuilderField}
              onCancelFieldEditing={cancelFieldEditing}
              onRemoveRuleClick={removeRule}
              onEditRuleClick={turnOnRuleEditingMode}
              onSaveEditingField={saveEditingFields}
              onRuleChange={updateRule}
            />
            <Styled.AddFieldsButton onClick={onAddFieldsButtonCLick}>
              + Add Fields
            </Styled.AddFieldsButton>
          </>
        )}
      </RuleBuilderDragDropContext>
    )
  );
}

StandardReportIncludedFields.defaultProps = {
  onAddFieldsButtonCLick: noop,
};

export default memo(StandardReportIncludedFields);
